import React from "react";
import AdminModal from "~/components/modal/AdminModal";
import { DictionaryObject } from "~/TTS/voicevox/VoicevoxUtils";
import { useDictionary } from "../settings/HamburgerMenu/hooks/useDictionary";
import "~/css/DictionaryForm.css";
import { useTranslation } from "react-i18next";

interface Props {
  dict: DictionaryObject | null;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
  registeredDicts?: DictionaryObject[];
}

/**
 * 辞書機能 編集モーダル
 */
const DictionaryEditModal: React.FC<Props> = ({
  dict,
  open,
  onClose,
  onUpdated,
  registeredDicts,
}) => {
  const { t } = useTranslation();
  const {
    word,
    kana,
    priority,
    accent,
    moras,
    isProcessing,
    setWord,
    setKana,
    setPriority,
    setAccent,
    preview,
    save,
    remove,
    cancel,
    suggestKana,
    markKanaEdited,
  } = useDictionary({
    mode: "edit",
    initialDict: dict ?? undefined,
    registeredDicts,
    onSaved: () => {
      onUpdated();
      onClose();
    },
    onDeleted: () => {
      onUpdated();
      onClose();
    },
    onCancel: onClose,
  });

  const content = (
    <div className="dict-form-wrapper edit-mode">
      <div className="dict-form-body">
        <div className="dict-form-field">
          <div className="form-row">
            <div className="field-inline">
              <span className="field-label">{t("dictionaryWord")}</span>
              <input
                type="text"
                name="word"
                className="form-input"
                placeholder={t("example", { attribute: t("exampleDictionaryWord") })}
                value={word}
                onChange={(e) => setWord(e.target.value.replace(/\s/g, ""))}
                onBlur={suggestKana}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="field-inline">
              <span className="field-label">{t("pronunciation")}</span>
              <input
                type="text"
                name="wordKana"
                className="form-input"
                placeholder={t("example", { attribute: "ヒマラヤサンミャク" })}
                value={kana}
                onChange={(e) => {
                  markKanaEdited();
                  setKana(e.target.value.replace(/\s/g, ""));
                }}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="field-inline">
              <span className="field-label">{t("priority")}</span>
              <div className="priority-row">
                <span className="priority-edge">{t("low")}</span>
                <div className="priority-slider-composite">
                  <div className="ps-track">
                    <div
                      className="ps-track-active"
                      style={{ width: `${((priority - 1) / 9) * 100}%` }}
                    />
                    <div className="ps-ticks" aria-hidden>
                      {Array.from({ length: 10 }, (_, i) => {
                        const pos = i + 1;
                        const active = pos <= priority;
                        const current = pos === priority;
                        return (
                          <span
                            key={pos}
                            className={`ps-tick ${active ? "active" : ""} ${current ? "current" : ""}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={10}
                    value={priority}
                    onChange={(e) => setPriority(Number(e.target.value))}
                    className="ps-range-overlay"
                    aria-label={`${t("priority")}`}
                  />
                </div>
                <span className="priority-edge">{t("high")}</span>
                <span className="priority-value">{priority}</span>
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="field-inline">
              <span className="field-label">{t("accent")}</span>
              <div className="accent-grid" style={{ flex: 1 }}>
                {moras.map((m, i) => {
                  const idx = i + 1;
                  return (
                    <div
                      key={i}
                      className={`accent-cell ${accent === idx ? "selected" : ""}`}
                      onClick={() => setAccent(idx)}
                    >
                      <span className="mora">{m}</span>
                      <input
                        type="radio"
                        name="accent"
                        checked={accent === idx}
                        onChange={() => setAccent(idx)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={cancel} disabled={isProcessing}>
            {t("cancel")}
          </button>
          <button type="button" className="btn-delete" onClick={remove} disabled={isProcessing}>
            {t("delete")}
          </button>
          <button type="button" className="btn-preview" onClick={preview} disabled={isProcessing}>
            {t("preview")}
          </button>
          <button type="button" className="btn-regist" onClick={save} disabled={isProcessing}>
            {t("update")}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <AdminModal title={t("dictionaryEditing")} isOpen={open}>
        {content}
      </AdminModal>
    </>
  );
};

export default DictionaryEditModal;
